import {html, LitElement} from 'lit';
import {customElement, property, queryAssignedElements} from 'lit/decorators.js';
import {Logger, PubSub} from '@mamdev/cad.static-next-lib';

import WindowHelper from '../../ts/services/window';
import styles from './social.styles';
import {SocialShare} from './social-share';
import Tracking2 from '../../ts/services/tracking2';

import type {ShareEventPayload} from './types';

@customElement('ui-social')
export class Social extends LitElement {
  static readonly styles = [styles];
  @property() brandname = '';
  @property() headline = '';
  @property() url = '';
  @queryAssignedElements({selector: 'ui-social-share', flatten: false}) socialShareButtons: SocialShare[];
  baseUrls = {
    facebookSharer: 'https://www.facebook.com/sharer/sharer.php',
    facebookBase: 'https://www.facebook.com/login.php',
    twitter: 'https://x.com/intent/tweet',
    whatsapp: 'whatsapp://send'
  };

  get componentPath() {
    return this.getAttribute('data-component-path') ?? '';
  }

  render() {
    return html`
      <slot @slotchange="${this.handleSlotChange}"></slot>
    `;
  }

  handleSlotChange() {
    this.socialShareButtons.forEach(socialShare => {
      socialShare.addEventListener('customEventSocialClick', (event: CustomEventInit<ShareEventPayload>) => {
        PubSub.publish('video:pauseAllPlayers');
        PubSub.publish('globalReco:userFeedback', 'socialShareCount');
        if (event.detail) {
          const options = event.detail;
          switch (options.type) {
            case 'facebook':
              this.shareViaFacebook();
              break;
            case 'twitter':
              this.shareViaTwitter(options.account ?? '', options.via);
              break;
            case 'mail':
              this.shareViaMail();
              break;
            case 'whatsapp':
              this.shareViaWhatsapp();
              break;
            case 'share':
              void this.shareViaNativeShare();
              break;
            default:
              break;
          }
        }
      });
    });
  }

  sendBrainTrackingEvent(clickName: string) {
    PubSub.publish('social:share', {
      referrer: '&referrer=undefined',
      click1: '&click1=undefined',
      click2: '&click2=undefined',
      click3: '&click3=undefined',
      click4: '&click4=undefined',
      type: '&type=hidden',
      clickname: '&clickname=' + clickName
    });
  }

  sendTrackingEvent(clickName: string) {
    Tracking2.callTealiumLink({
      businessEventType: 'userAction',
      componentPath: `${this.componentPath}.${clickName}`,
      componentName: `share-icon.${clickName}`,
      elementTagName: 'button',
      eventType: 'mousedown'
    });
  }

  shareViaWhatsapp() {
    this.sendBrainTrackingEvent('whatsapp');
    this.sendTrackingEvent('whatsapp');
    const uri = new URL(this.baseUrls.whatsapp);
    uri.searchParams.append(
      'text',
      `Hallo,\n\nich habe gerade diesen interessanten Beitrag bei ${this.brandname}
       gefunden: ${this.headline} - ${this.url}#.wa.whatsapp`
    );
    window.location.href = uri.toString();
  }

  shareViaMail() {
    const linebreak = '%0D%0A';
    const subject = encodeURIComponent('Leseempfehlung: ' + this.headline);
    let txt = 'Hallo%2C%0A%0Aich%20habe%20gerade%20diesen%20interessanten%20Beitrag%20bei%20'
      + encodeURIComponent(this.brandname + ' gefunden:') + linebreak;
    txt += encodeURIComponent(this.headline) + linebreak;
    txt += encodeURIComponent(this.url + '#.wa.mailsent') + linebreak + linebreak;
    txt += encodeURIComponent('Weitere aktuelle News bei ' + this.brandname + ' ' + window.ui.pageinfo.home);

    this.sendBrainTrackingEvent('sendmail');
    this.sendTrackingEvent('mail');

    let uri = 'mailto:?subject=';
    uri += subject;
    uri += '&body=';
    uri += txt;
    window.location.href = uri;
  }

  /**
   * Create post popup for facebook using the simple fb url api (without sdk)
   * @mask https://www.facebook.com/login.php?next=https://www.facebook.com/sharer/sharer.php?u=<url>
   *   &t=<title>&rel=login&display=popup
   */
  shareViaFacebook() {
    this.sendBrainTrackingEvent('facebook_recommend');
    this.sendTrackingEvent('facebook');

    // Cleanup Development URLs (cannot be reached from Facebook)
    const cleanUrl = this.url.replace(/(\/\/dev\.)|(\/\/test\.)|(\/\/pre\.)|(\/\/local\.)|(\/\/red\.)|(\/\/pattern-lab\.dev\.)/gi, '//');
    const sharer = new URL(this.baseUrls.facebookSharer);
    sharer.searchParams.append('u', cleanUrl + '#.wa.facebook');
    sharer.searchParams.append('t', this.headline + ' | ' + this.brandname);
    sharer.searchParams.append('rel', 'login');
    sharer.searchParams.append('display', 'popup');

    const uri = `${this.baseUrls.facebookBase}?next=${sharer.toString()}`;

    WindowHelper.windowOpenExtended(uri, 675, 375);
  }

  shareViaTwitter(account: string, via: string | undefined) {
    if (via) {
      this.sendBrainTrackingEvent('tweet');
      this.sendTrackingEvent('twitter');
      const uri = new URL(this.baseUrls.twitter);
      uri.searchParams.append('url', this.url + '#.wa.twitter');

      if (!account) {
        uri.searchParams.append('text', this.headline + via);
      } else {
        uri.searchParams.append('via', account);
        uri.searchParams.append('text', this.headline);
      }

      WindowHelper.windowOpenExtended(uri.toString(), 550, 460);
    }
  }

  async shareViaNativeShare() {
    this.sendBrainTrackingEvent('share_open');
    this.sendTrackingEvent('native-share');

    try {
      await navigator.share({title: document.title, url: this.url});
      this.sendBrainTrackingEvent('share_success');
    } catch (e) {
      Logger.logUnknownError(e, 'Social');
      this.sendBrainTrackingEvent('share_error');
    }
  }
}
